import { createApiSlice } from '@/shared/features/query/config/query.build';
import type { ReduxQueryClient } from '@/shared/features/query/domain/interfaces/QueryClient';
import type { RtkQueryAdapter } from '@/shared/features/query/infra/adapters/rtk.adapter';
import type { UserContextResponseDTO } from '../dto/userContext.dto';
import { adaptUserContextResponseDtoToDomain, userContextToPlainObject } from '../mappers/userContext.mapper';

/**
 * User Context API Slice
 * Fetches user-specific context data for header (login state, counts)
 */
const userContextApiSlice = createApiSlice({
  clientId: 'userContextApi',
  tagTypes: ['UserContext'],
  endpoints: {
    getUserContext: {
      url: '/user-context',
      method: 'GET',
      // User context is session-based; it needs cookies.
      withCredentials: true,
      transformResponse: (response: unknown): Record<string, unknown> => {
        // DTO → Domain (applies domain logic) → Plain Object (for Redux)
        // Keep `wishListDesignIds` in Redux so wishlistCount can always be derived from its length.
        const dto = (typeof response === 'object' && response !== null ? response : {}) as UserContextResponseDTO;
        const domainModel = adaptUserContextResponseDtoToDomain(dto);
        return {
          ...userContextToPlainObject(domainModel),
          wishListDesignIds: Array.isArray(dto.wishListDesignIds) ? dto.wishListDesignIds : [],
          // Analytics / eventLogger need these server fields (not on domain model today).
          ...(dto.customerId != null ? { customerId: dto.customerId } : {}),
          ...(typeof dto.pincode === 'string' ? { pincode: dto.pincode } : {}),
          ...(typeof dto.guessedPincode === 'boolean'
            ? { guessedPincode: dto.guessedPincode }
            : {}),
          ...(typeof dto.userCode === 'string' && dto.userCode.trim()
            ? { userCode: dto.userCode.trim() }
            : {}),
          ...(typeof dto.phoneNumber === 'string' ? { phoneNumber: dto.phoneNumber } : {}),
          ...(typeof dto.email === 'string' ? { email: dto.email } : {}),
          ...(typeof dto.cityName === 'string' ? { cityName: dto.cityName } : {}),
          ...(typeof dto.state === 'string' ? { state: dto.state } : {}),
          ...(typeof dto.country === 'string' ? { country: dto.country } : {}),
          ...(typeof dto.firstName === 'string' && dto.firstName.trim()
            ? { firstName: dto.firstName.trim() }
            : {}),
          ...(typeof dto.middleName === 'string' && dto.middleName.trim()
            ? { middleName: dto.middleName.trim() }
            : {}),
          ...(typeof dto.lastName === 'string' && dto.lastName.trim()
            ? { lastName: dto.lastName.trim() }
            : {}),
        };
      },
      providesTags: ['UserContext'],
    },
  },
});

export const userContextApi = userContextApiSlice.queryClient;
export const userContextApiRedux = userContextApiSlice.queryClient as ReduxQueryClient;

/** RTK Query API instance for cache invalidation (e.g. after login). Use with dispatch(api.util.invalidateTags(['UserContext'])). */
export const userContextRtkApi = (userContextApiSlice.queryClient as RtkQueryAdapter).getApi();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const hooks = userContextApiSlice.hooks as any;
export const useGetUserContextQuery = hooks?.useGetUserContextQuery;
export const useLazyGetUserContextQuery = hooks?.useLazyGetUserContextQuery;

export default userContextApiSlice;
